'use client'

import { motion } from 'motion/react'
import dynamic from 'next/dynamic'
import { useMemo } from 'react'
import { getCdnPath } from '~/lib/utils'
import { FeatureCard } from './feature-card'

function TechnologyDiagram() {
  // Memoize all random values and icons to prevent regeneration on every render
  const { shuffledIcons, animations } = useMemo(() => {
    const devIcons = [
      { src: '/dev-icons/babel.svg', alt: 'Babel' },
      { src: '/dev-icons/biomejs.svg', alt: 'Biome' },
      { src: '/dev-icons/bun.svg', alt: 'Bun' },
      { src: '/dev-icons/cloudflare-workers.svg', alt: 'Cloudflare Workers' },
      { src: '/dev-icons/deepseek.svg', alt: 'DeepSeek' },
      { src: '/dev-icons/deno.svg', alt: 'Deno' },
      { src: '/dev-icons/django.svg', alt: 'Django' },
      { src: '/dev-icons/fastify.svg', alt: 'Fastify' },
      { src: '/dev-icons/firebase.svg', alt: 'Firebase' },
      { src: '/dev-icons/flask-light.svg', alt: 'Flask' },
      { src: '/dev-icons/flutter.svg', alt: 'Flutter' },
      { src: '/dev-icons/fly.svg', alt: 'Fly.io' },
      { src: '/dev-icons/gemini.svg', alt: 'Google Gemini' },
      { src: '/dev-icons/git.svg', alt: 'Git' },
      { src: '/dev-icons/github_light.svg', alt: 'GitHub' },
      { src: '/dev-icons/gitlab.svg', alt: 'GitLab' },
      { src: '/dev-icons/golang.svg', alt: 'Go' },
      { src: '/dev-icons/google-cloud.svg', alt: 'Google Cloud' },
      { src: '/dev-icons/grafana.svg', alt: 'Grafana' },
      { src: '/dev-icons/hono.svg', alt: 'Hono' },
      { src: '/dev-icons/hugging_face.svg', alt: 'Hugging Face' },
      { src: '/dev-icons/intellijidea.svg', alt: 'IntelliJ IDEA' },
      { src: '/dev-icons/java.svg', alt: 'Java' },
      { src: '/dev-icons/javascript.svg', alt: 'JavaScript' },
      { src: '/dev-icons/jest.svg', alt: 'Jest' },
      { src: '/dev-icons/jwt.svg', alt: 'JWT' },
      { src: '/dev-icons/kotlin.svg', alt: 'Kotlin' },
      { src: '/dev-icons/linux.svg', alt: 'Linux' },
      { src: '/dev-icons/mysql.svg', alt: 'MySQL' },
      { src: '/dev-icons/n8n-wordmark-light.svg', alt: 'n8n' },
      { src: '/dev-icons/nestjs.svg', alt: 'NestJS' },
      { src: '/dev-icons/nextjs_icon_dark.svg', alt: 'Next.js' },
      { src: '/dev-icons/nodejs.svg', alt: 'Node.js' },
      { src: '/dev-icons/ollama_light.svg', alt: 'Ollama' },
      { src: '/dev-icons/openai.svg', alt: 'OpenAI' },
      { src: '/dev-icons/php.svg', alt: 'PHP' },
      { src: '/dev-icons/playwright.svg', alt: 'Playwright' },
      { src: '/dev-icons/pnpm.svg', alt: 'pnpm' },
      { src: '/dev-icons/postgresql.svg', alt: 'PostgreSQL' },
      { src: '/dev-icons/postman.svg', alt: 'Postman' },
      { src: '/dev-icons/python.svg', alt: 'Python' },
      { src: '/dev-icons/raspberry_pi.svg', alt: 'Raspberry Pi' },
      { src: '/dev-icons/react_light.svg', alt: 'React' },
      { src: '/dev-icons/reactquery.svg', alt: 'TanStack Query' },
      { src: '/dev-icons/redis.svg', alt: 'Redis' },
      { src: '/dev-icons/redux.svg', alt: 'Redux' },
      { src: '/dev-icons/rolldown.svg', alt: 'Rolldown' },
      { src: '/dev-icons/ruby.svg', alt: 'Ruby' },
      { src: '/dev-icons/rust.svg', alt: 'Rust' },
      { src: '/dev-icons/scala.svg', alt: 'Scala' },
      { src: '/dev-icons/stackoverflow.svg', alt: 'Stack Overflow' },
      { src: '/dev-icons/supabase.svg', alt: 'Supabase' },
      { src: '/dev-icons/svelte.svg', alt: 'Svelte' },
      { src: '/dev-icons/swagger.svg', alt: 'Swagger' },
      { src: '/dev-icons/tailwindcss.svg', alt: 'Tailwind CSS' },
      { src: '/dev-icons/tanstack.svg', alt: 'TanStack' },
      { src: '/dev-icons/terraform.svg', alt: 'Terraform' },
      { src: '/dev-icons/trpc.svg', alt: 'tRPC' },
      { src: '/dev-icons/turso-light.svg', alt: 'Turso' },
      { src: '/dev-icons/typescript.svg', alt: 'TypeScript' },
      { src: '/dev-icons/ubuntu.svg', alt: 'Ubuntu' },
      { src: '/dev-icons/vim.svg', alt: 'Vim' },
      { src: '/dev-icons/vitejs.svg', alt: 'Vite' },
      { src: '/dev-icons/vitest.svg', alt: 'Vitest' },
      { src: '/dev-icons/vscode.svg', alt: 'VS Code' },
      { src: '/dev-icons/webkit.svg', alt: 'WebKit' },
      { src: '/dev-icons/wordpress.svg', alt: 'WordPress' },
    ]

    const shuffled = [...devIcons].sort(() => Math.random() - 0.5)

    // Pre-generate all random animation values
    const randomDelay = (min: number, max: number) => Math.random() * (max - min) + min
    const randomDuration = (min: number, max: number) => Math.random() * (max - min) + min

    // Generate animation values for rectangles (9 total) and paths/lines (9 total)
    const rectAnimations = Array.from({ length: 9 }, () => ({
      duration: randomDuration(0.4, 0.8),
      delay: randomDelay(0, 0.6),
    }))

    const pathAnimations = Array.from({ length: 9 }, () => ({
      duration: randomDuration(0.6, 1.2),
      delay: randomDelay(0.3, 1.0),
    }))

    return {
      shuffledIcons: shuffled,
      animations: {
        rects: rectAnimations,
        paths: pathAnimations,
      },
    }
  }, []) // Empty dependency array - compute only once on mount

  // Helper to get path animation by index
  const getPathAnimation = (index: number) => animations.paths[index] || animations.paths[0]

  // function to create a rectangle with image
  const createImageRect = (x: number, y: number, iconIndex: number) => {
    const icon = shuffledIcons[iconIndex] || shuffledIcons[0]
    const animation = animations.rects[iconIndex] || animations.rects[0]

    return (
      <motion.g
        initial={{ opacity: 0, scale: 0.8 }}
        transition={{
          duration: animation.duration,
          delay: animation.delay,
        }}
        viewport={{ once: true }}
        whileInView={{ opacity: 1, scale: 1 }}
      >
        <rect fill="none" height="126" rx="15.5" stroke="currentColor" width="126" x={x} y={y} />
        <foreignObject x={x} y={y} width="126" height="126">
          <div className="relative size-full overflow-hidden rounded-2xl p-4">
            <img src={getCdnPath(icon.src)} alt={icon.alt} className="size-full rounded-xl" />
          </div>
        </foreignObject>
      </motion.g>
    )
  }

  return (
    <motion.svg
      className="pointer-events-none -translate-x-4 -translate-y-12 -rotate-12 text-muted-foreground"
      fill="none"
      height="500"
      initial={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      viewBox="0 0 1067 500"
      viewport={{ once: true, amount: 0.3 }}
      whileInView={{ opacity: 0.4 }}
      width="1067"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Technology ecosystem diagram</title>

      {/* Package rectangles with staggered animations */}
      {createImageRect(470.5, 356.5, 0)}
      {createImageRect(705.5, 178.5, 1)}
      {createImageRect(470.5, 178.5, 2)}
      {createImageRect(470.5, 0.5, 3)}
      {createImageRect(0.5, 0.5, 4)}
      {createImageRect(705.5, 0.5, 5)}
      {createImageRect(235.5, 0.5, 6)}
      {createImageRect(940.5, 0.5, 7)}
      {createImageRect(235.5, 178.5, 8)}

      {/* Connecting lines and paths */}
      <motion.path
        d="M298.278 304.584V304.584C298.278 368.097 349.765 419.584 413.278 419.584L470.278 419.584"
        fill="none"
        initial={{ pathLength: 0, opacity: 0 }}
        stroke="currentColor"
        strokeDasharray="2 2"
        transition={getPathAnimation(0)}
        viewport={{ once: true }}
        whileInView={{ pathLength: 1, opacity: 1 }}
      />
      <motion.path
        d="M768.279 305.415V305.415C768.279 368.928 716.792 420.415 653.279 420.415L597.279 420.415"
        fill="none"
        initial={{ pathLength: 0, opacity: 0 }}
        stroke="currentColor"
        strokeDasharray="2 2"
        transition={getPathAnimation(1)}
        viewport={{ once: true }}
        whileInView={{ pathLength: 1, opacity: 1 }}
      />
      <motion.line
        initial={{ pathLength: 0, opacity: 0 }}
        stroke="currentColor"
        strokeDasharray="2 2"
        transition={getPathAnimation(2)}
        viewport={{ once: true }}
        whileInView={{ pathLength: 1, opacity: 1 }}
        x1="533.5"
        x2="533.5"
        y1="356"
        y2="305"
      />
      <motion.path
        d="M234.722 242.416L177.722 242.416C114.209 242.416 62.722 190.929 62.7219 127.416V127.416"
        fill="none"
        initial={{ pathLength: 0, opacity: 0 }}
        stroke="currentColor"
        strokeDasharray="2 2"
        transition={getPathAnimation(3)}
        viewport={{ once: true }}
        whileInView={{ pathLength: 1, opacity: 1 }}
      />
      <motion.path
        d="M1003.28 127.415V127.415C1003.28 190.928 951.792 242.415 888.279 242.415L832.279 242.415"
        fill="none"
        initial={{ pathLength: 0, opacity: 0 }}
        stroke="currentColor"
        strokeDasharray="2 2"
        transition={getPathAnimation(4)}
        viewport={{ once: true }}
        whileInView={{ pathLength: 1, opacity: 1 }}
      />
      <motion.line
        initial={{ pathLength: 0, opacity: 0 }}
        stroke="currentColor"
        strokeDasharray="2 2"
        transition={getPathAnimation(5)}
        viewport={{ once: true }}
        whileInView={{ pathLength: 1, opacity: 1 }}
        x1="768.5"
        x2="768.5"
        y1="127"
        y2="178"
      />
      <motion.line
        initial={{ pathLength: 0, opacity: 0 }}
        stroke="currentColor"
        strokeDasharray="2 2"
        transition={getPathAnimation(6)}
        viewport={{ once: true }}
        whileInView={{ pathLength: 1, opacity: 1 }}
        x1="533.5"
        x2="533.5"
        y1="127"
        y2="178"
      />
      <motion.line
        initial={{ pathLength: 0, opacity: 0 }}
        stroke="currentColor"
        strokeDasharray="2 2"
        transition={getPathAnimation(7)}
        viewport={{ once: true }}
        whileInView={{ pathLength: 1, opacity: 1 }}
        x1="298.5"
        x2="298.5"
        y1="127"
        y2="178"
      />
      <motion.path
        d="M533.104 127.489C533.104 127.489 533.104 152.489 415.604 152.489C298.104 152.489 298.104 177.489 298.104 177.489"
        fill="none"
        initial={{ pathLength: 0, opacity: 0 }}
        stroke="currentColor"
        strokeDasharray="2 2"
        transition={getPathAnimation(8)}
        viewport={{ once: true }}
        whileInView={{ pathLength: 1, opacity: 1 }}
      />
      <motion.path
        d="M768.106 127.488C768.106 127.488 768.106 152.989 651.106 152.988C534.106 152.988 534.106 178.488 534.106 178.488"
        fill="none"
        initial={{ pathLength: 0, opacity: 0 }}
        stroke="currentColor"
        strokeDasharray="2 2"
        transition={getPathAnimation(0)}
        viewport={{ once: true }}
        whileInView={{ pathLength: 1, opacity: 1 }}
      />
    </motion.svg>
  )
}

// dynamic import to disable Next.js caching for random animations
const TechnologyDiagramNoSSR = dynamic(() => Promise.resolve(TechnologyDiagram), {
  ssr: false,
})

interface InterviewFlashCardsProps {
  className?: string
}

export function InterviewFlashCards(props: InterviewFlashCardsProps) {
  return (
    <FeatureCard
      background={<TechnologyDiagramNoSSR />}
      title="Тренажер"
      description="Интерактивные флеш-карточки с вопросами из базы и готовыми пресетами по компаниям и уровням. Персональные наборы, отслеживание прогресса, адаптивный алгоритм повторений и детальная статистика по темам."
      className={props.className}
    />
  )
}
